import { requireSupabase } from '@/lib/supabase';
import type { Review, ReviewStatus } from '@/domain/types';

type ReviewRow = {
  id: string;
  product_id: string;
  products?: { name?: string | null } | null;
  user_id: string | null;
  rating: number;
  title: string;
  content: string;
  status: string;
  is_manual: boolean;
  reviewer_name: string;
  reviewer_role: string;
  reviewer_avatar: string;
  created_at: string;
};

function mapReview(row: ReviewRow): Review {
  return {
    id: row.id,
    productId: row.product_id,
    productName: row.products?.name ?? undefined,
    userId: row.user_id,
    reviewerName: row.reviewer_name,
    reviewerRole: row.reviewer_role,
    reviewerAvatar: row.reviewer_avatar,
    rating: row.rating,
    title: row.title,
    content: row.content,
    status: row.status as ReviewStatus,
    isManual: row.is_manual,
    createdAt: row.created_at,
  };
}

export async function getProductReviews(productId: string): Promise<Review[]> {
  const supabase = requireSupabase();
  const { data, error } = await supabase
    .from('reviews')
    .select('*, profiles!user_id(name, avatar_url)')
    .eq('product_id', productId)
    .eq('status', 'approved')
    .order('created_at', { ascending: false });
  if (error) throw error;
  return ((data as any[]) ?? []).map((row) => ({
    ...mapReview(row as ReviewRow),
    // for user-submitted reviews, fall back to their profile name/avatar
    reviewerName: row.reviewer_name || row.profiles?.name || 'Anonymous',
    reviewerAvatar: row.reviewer_avatar || row.profiles?.avatar_url || '',
  }));
}

export async function checkUserPurchased(productId: string): Promise<boolean> {
  const supabase = requireSupabase();
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return false;
  const { data, error } = await supabase
    .from('licenses')
    .select('id')
    .eq('product_id', productId)
    .eq('user_id', user.id)
    .in('status', ['active', 'grace'])
    .limit(1)
    .maybeSingle();
  if (error) return false;
  return data !== null;
}

export interface SubmitReviewInput {
  productId: string;
  rating: number;
  title: string;
  content: string;
}

export async function submitReview(input: SubmitReviewInput): Promise<void> {
  const supabase = requireSupabase();
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('You must be logged in to submit a review.');
  const { error } = await supabase
    .from('reviews')
    .insert({
      product_id: input.productId,
      user_id: user.id,
      rating: input.rating,
      title: input.title,
      content: input.content,
      status: 'pending',
      is_manual: false,
      reviewer_name: '',
      reviewer_role: '',
      reviewer_avatar: '',
    });
  if (error) throw error;
}

export interface AdminReviewInput {
  productId: string;
  rating: number;
  title: string;
  content: string;
  reviewerName: string;
  reviewerRole: string;
  reviewerAvatar: string;
  status: ReviewStatus;
}

export async function listAdminReviews(): Promise<Review[]> {
  const supabase = requireSupabase();
  const { data, error } = await supabase
    .from('reviews')
    .select('*, products!product_id(name)')
    .order('created_at', { ascending: false });
  if (error) throw error;
  return ((data as ReviewRow[]) ?? []).map(mapReview);
}

export async function createAdminReview(input: AdminReviewInput): Promise<void> {
  const supabase = requireSupabase();
  const { error } = await supabase
    .from('reviews')
    .insert({
      product_id: input.productId,
      user_id: null,
      rating: input.rating,
      title: input.title,
      content: input.content,
      status: input.status,
      is_manual: true,
      reviewer_name: input.reviewerName,
      reviewer_role: input.reviewerRole,
      reviewer_avatar: input.reviewerAvatar,
    });
  if (error) throw error;
}

export async function updateAdminReviewStatus(id: string, status: ReviewStatus): Promise<void> {
  const supabase = requireSupabase();
  const { error } = await supabase
    .from('reviews')
    .update({ status })
    .eq('id', id);
  if (error) throw error;
}

export async function deleteAdminReview(id: string): Promise<void> {
  const supabase = requireSupabase();
  const { error } = await supabase
    .from('reviews')
    .delete()
    .eq('id', id);
  if (error) throw error;
}
